import React, { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { useLang } from "../context/LangContext";
import { ASSETS } from "../lib/content";
import { pathFor } from "../lib/routes";
import { ArrowRight } from "lucide-react";

const EASE = [0.22, 1, 0.36, 1];

function MaskedTitle({ text, className, style }) {
  const words = text.split(" ");
  return (
    <h1 className={className} style={style} aria-label={text}>
      {words.map((w, i) => (
        <span key={i} className="inline-block overflow-hidden align-bottom pb-[0.1em] -mb-[0.1em]" aria-hidden="true">
          <motion.span
            className="inline-block will-change-transform"
            initial={{ y: "115%" }}
            animate={{ y: "0%" }}
            transition={{ duration: 1, delay: 0.35 + i * 0.06, ease: EASE }}
          >
            {w}{i < words.length - 1 ? " " : ""}
          </motion.span>
        </span>
      ))}
    </h1>
  );
}

export default function Hero() {
  const { lang, t } = useLang();
  const p = (k) => pathFor(k, lang);
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "20%"]);
  const bgScale = useTransform(scrollYProgress, [0, 1], [1.06, 1.18]);
  const fgOpacity = useTransform(scrollYProgress, [0, 0.75], [1, 0]);
  const fgY = useTransform(scrollYProgress, [0, 1], ["0%", "28%"]);

  return (
    <section ref={ref} className="relative min-h-[100svh] w-full overflow-hidden bg-base" data-testid="hero-section">
      <motion.div className="absolute inset-0 will-change-transform" style={{ y: bgY, scale: bgScale }}>
        <img src={ASSETS.heroBg} alt="Monaco superyacht at dusk" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(15,37,64,0.35) 0%, rgba(15,37,64,0.45) 40%, rgba(245,241,232,0.95) 100%)" }} />
      </motion.div>

      <motion.div style={{ opacity: fgOpacity, y: fgY }} className="relative z-10 container-luxe pt-40 md:pt-48 pb-24 min-h-[100svh] flex flex-col justify-between">
        <div className="max-w-4xl">
          <motion.div
            className="overline mb-6"
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.15, ease: EASE }}
          >
            {t("home.eyebrow")}
          </motion.div>
          <MaskedTitle
            text={t("home.hero_title")}
            className="font-display text-4xl sm:text-6xl lg:text-7xl tracking-tighter font-light text-white leading-[0.98]"
            style={{ textShadow: "0 2px 24px rgba(15,37,64,0.45)" }}
          />
          <motion.p
            className="mt-7 max-w-2xl text-base sm:text-lg text-white/85 font-body leading-relaxed"
            style={{ textShadow: "0 1px 12px rgba(15,37,64,0.45)" }}
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1.05, ease: EASE }}
          >
            {t("home.hero_sub")}
          </motion.p>
          <motion.div
            className="mt-9 flex flex-wrap gap-3"
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1.2, ease: EASE }}
          >
            <Link to={p("booking")} className="btn-gold" data-testid="hero-cta-book">
              {t("cta.book_vip")} <ArrowRight size={14} />
            </Link>
            <Link to={p("treatments")} className="btn-ghost" data-testid="hero-cta-explore">
              {t("cta.explore")}
            </Link>
          </motion.div>
        </div>

        <div className="mt-20 grid grid-cols-2 md:grid-cols-4 gap-px hairline-t">
          {[
            { k: t("stats.pain_reduction"), v: "-72%" },
            { k: t("stats.rom_gain"), v: "+38%" },
            { k: t("stats.crew_uptime"), v: "+24%" },
            { k: t("stats.return_rate"), v: "94%" },
          ].map((s, i) => (
            <motion.div
              key={s.k}
              className="px-5 py-6 bg-[var(--bg-base)]/90 backdrop-blur-sm"
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 1.35 + i * 0.1, ease: EASE }}
            >
              <div className="font-display text-2xl md:text-3xl text-[var(--ink)] font-light">{s.v}</div>
              <div className="overline mt-1">{s.k}</div>
            </motion.div>
          ))}
        </div>
      </motion.div>
    </section>
  );
}
